import React from 'react';
import {view, Icon, Link} from '@sail/ui';
import {InlineDatumReactComponentProps} from '@stripe-internal/inline-data';
import {
  LightCard,
  ThemeProvider,
  LinkTheme,
} from '@stripe-internal/conversations-ui';
import {defineMessages, FormattedMessage} from 'react-intl';
import {business} from '@sail/icons/react/Icon';

export type ValueProps = {
  merchant_name: string;
  merchant_avatar: string;
  merchant_website: string;
  merchant_email: string;
  merchant_phone: string;
};

const messages = defineMessages({
  websiteTitle: {
    id: 'mse_inline_data.universal_help_business_detail.website_title',
    description: 'Heading for business website section',
    defaultMessage: 'Website',
  },
  emailTitle: {
    id: 'mse_inline_data.universal_help_business_detail.email_title',
    description: 'Heading for business email section',
    defaultMessage: 'Email',
  },
  phoneNumberTitle: {
    id: 'mse_inline_data.universal_help_business_detail.phone_number_title',
    description: 'Heading for business phone number section',
    defaultMessage: 'Phone number',
  },
  noAdditionalBusinessDetails: {
    id: 'mse_inline_data.universal_help_business_detail.no_additional_business_details',
    description:
      'Message directing user to contact the business when no additonal details available.',
    defaultMessage:
      'We do not have additional details of {merchant}, you will need to reach out to the business directly for help.',
  },
});

const LinkBusinessDetails = ({
  merchantPropsValue,
}: {
  merchantPropsValue: ValueProps;
}) => {
  const {
    merchant_name: merchantName,
    merchant_website: merchantWebsite,
    merchant_email: merchantEmail,
    merchant_phone: merchantPhone,
  } = merchantPropsValue;

  const hasContactInfo = merchantWebsite || merchantPhone || merchantEmail;

  if (!hasContactInfo) {
    return (
      <view.div css={{gap: 'medium', stack: 'y'}}>
        <view.div data-testid="no-additional-merchant-details">
          <FormattedMessage
            {...messages.noAdditionalBusinessDetails}
            values={{merchant: merchantName}}
          />
        </view.div>
      </view.div>
    );
  }

  return (
    <view.div>
      <view.div css={{font: 'label.medium.emphasized'}}>
        <FormattedMessage {...messages.websiteTitle} />
      </view.div>
      <view.div
        data-testid="merchant-website"
        css={{
          font: 'label.medium',
          marginBottom: 'medium',
        }}
      >
        <Link
          type="primary"
          href={merchantWebsite}
          target="_blank"
          rel="noopener noreferrer"
          css={{color: 'rgb(0, 102, 53)'}}
        >
          {merchantWebsite}
        </Link>
      </view.div>

      <view.div css={{font: 'label.medium.emphasized'}}>
        <FormattedMessage {...messages.emailTitle} />
      </view.div>
      <view.div
        data-testid="merchant-email"
        css={{
          font: 'label.medium',
          marginBottom: 'medium',
        }}
      >
        <Link
          type="primary"
          href={`mailto:${merchantEmail}`}
          css={{color: 'rgb(0, 102, 53)'}}
        >
          {merchantEmail}
        </Link>
      </view.div>

      <view.div css={{font: 'label.medium.emphasized'}}>
        <FormattedMessage {...messages.phoneNumberTitle} />
      </view.div>
      <view.div
        data-testid="merchant-phone"
        css={{
          font: 'label.medium',
          marginBottom: 'medium',
        }}
      >
        <Link
          type="primary"
          href={`tel:${merchantPhone}`}
          css={{color: 'rgb(0, 102, 53)'}}
        >
          {merchantPhone}
        </Link>
      </view.div>
    </view.div>
  );
};

const LinkBusinessCardDetail = ({
  props,
}: {
  props: InlineDatumReactComponentProps;
}) => {
  const {datum} = props;
  const merchantPropsValue = datum.value as ValueProps;
  const {merchant_name: merchantName, merchant_avatar: merchantAvatar} =
    merchantPropsValue;

  return (
    <view.div css={{gap: 'medium', stack: 'y'}}>
      <LightCard isOuter>
        <view.div css={{stack: 'y', gap: 'large'}}>
          <view.div css={{stack: 'x', gap: 'small'}}>
            <view.div
              css={{
                stack: 'x',
                width: 'large',
                height: 'large',
                alignSelfY: 'center',
              }}
            >
              <view.div
                css={{
                  borderRadius: '50%',
                  border: '1px inset rgba(0, 0, 0, 0.15)',
                  width: 'fill',
                  height: 'fill',
                }}
              >
                {merchantAvatar ? (
                  <view.img
                    data-testid="merchant-avatar"
                    src={merchantAvatar}
                    css={{
                      width: 'large',
                      height: 'large',
                      borderRadius: '50%',
                      border: '1px inset rgba(0, 0, 0, 0.15)',
                    }}
                  />
                ) : (
                  <Icon
                    data-testid="merchant-avatar"
                    icon={business}
                    css={{
                      padding: 'xsmall',
                      width: 'fill',
                      height: 'fill',
                    }}
                  />
                )}
              </view.div>
            </view.div>

            <view.h3
              css={{margin: 0, font: 'heading.large'}}
              data-testid="merchant-name"
            >
              {merchantName}
            </view.h3>
          </view.div>

          <LinkBusinessDetails merchantPropsValue={merchantPropsValue} />
        </view.div>
      </LightCard>
    </view.div>
  );
};

export const LinkBusinessCard = (props: InlineDatumReactComponentProps) => {
  return (
    <ThemeProvider value={LinkTheme}>
      <LinkBusinessCardDetail props={props} />
    </ThemeProvider>
  );
};
